import { useState, useEffect, useRef, useCallback } from 'react';
import { getStationStatus, getNowPlaying, getRadioOnAir } from '@/services/nowPlaying';
import type {
  StationStatusResult,
  NowPlayingResult,
  RadioOnAirResult,
} from '@/types/nowPlaying';

// ── INTERVALOS ──

const NOW_PLAYING_INTERVAL_MS = 12_000;
const STATION_STATUS_INTERVAL_MS = 25_000;
const MAX_CONSECUTIVE_ERRORS = 3;

// ── RESULTADO DEL HOOK ──

export interface UseNowPlayingResult {
  /** Estado operacional de la estación */
  status: StationStatusResult | RadioOnAirResult | null;
  /** Metadata de la pista actual */
  nowPlaying: NowPlayingResult | RadioOnAirResult | null;
  /** true mientras se ejecuta la primera carga */
  isLoading: boolean;
  /** Error del último intento fallido (sanitizado) */
  error: string | null;
  /** true si los datos están desactualizados según el backend */
  isStale: boolean;
  /** Label amigable del estado online/offline */
  onlineLabel: string;
  /** Refrescar manualmente ambos endpoints */
  refresh: () => void;
}

export interface UseNowPlayingOptions {
  stationId?: string | null;
  radioId?: string | null;
}

// ── HOOK ──

export function useNowPlaying({ stationId, radioId }: UseNowPlayingOptions): UseNowPlayingResult {
  const [status, setStatus] = useState<StationStatusResult | RadioOnAirResult | null>(null);
  const [nowPlaying, setNowPlaying] = useState<NowPlayingResult | RadioOnAirResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Refs para evitar condiciones de carrera ──
  const mountedRef = useRef(true);
  const stationIdRef = useRef<string | null>(stationId ?? null);
  const radioIdRef = useRef<string | null>(radioId ?? null);
  const nowPlayingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const statusTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const errorCountRef = useRef(0);
  const isTabVisibleRef = useRef(true);
  const isFetchingRef = useRef(false);

  // Actualizar refs cuando cambian los IDs
  useEffect(() => {
    stationIdRef.current = stationId ?? null;
  }, [stationId]);

  useEffect(() => {
    radioIdRef.current = radioId ?? null;
  }, [radioId]);

  const activeId = stationId ?? radioId ?? null;

  // ── Fetch functions ──

  const fetchNowPlaying = useCallback(async () => {
    const sid = stationIdRef.current;
    const rid = radioIdRef.current;
    if (!mountedRef.current || isFetchingRef.current) return;
    if (!isTabVisibleRef.current) return;

    isFetchingRef.current = true;

    try {
      if (sid) {
        const data = await getNowPlaying(sid);
        if (mountedRef.current && stationIdRef.current === sid) {
          setNowPlaying(data);
          setError(null);
          errorCountRef.current = 0;
        }
      } else if (rid) {
        const data = await getRadioOnAir(rid);
        if (mountedRef.current && radioIdRef.current === rid) {
          setNowPlaying(data);
          setError(null);
          errorCountRef.current = 0;
        }
      }
    } catch (err: unknown) {
      if (mountedRef.current) {
        errorCountRef.current += 1;
        if (errorCountRef.current >= MAX_CONSECUTIVE_ERRORS) {
          setError(err instanceof Error ? err.message : 'Error al consultar now-playing');
        }
      }
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  const fetchStationStatus = useCallback(async () => {
    const sid = stationIdRef.current;
    const rid = radioIdRef.current;
    if (!mountedRef.current) return;
    if (!isTabVisibleRef.current) return;

    try {
      if (sid) {
        const data = await getStationStatus(sid);
        if (mountedRef.current && stationIdRef.current === sid) {
          setStatus(data);
          setError(null);
        }
      } else if (rid) {
        const data = await getRadioOnAir(rid);
        if (mountedRef.current && radioIdRef.current === rid) {
          setStatus(data);
          setError(null);
        }
      }
    } catch (err: unknown) {
      if (mountedRef.current) {
        if (!status) {
          setError(err instanceof Error ? err.message : 'Error al consultar estado');
        }
      }
    }
  }, [status]);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    errorCountRef.current = 0;
    await Promise.allSettled([fetchNowPlaying(), fetchStationStatus()]);
    if (mountedRef.current) {
      setIsLoading(false);
    }
  }, [fetchNowPlaying, fetchStationStatus]);

  // ── Polling setup ──

  useEffect(() => {
    mountedRef.current = true;

    if (!activeId) {
      setIsLoading(false);
      setStatus(null);
      setNowPlaying(null);
      setError(null);
      return;
    }

    // Limpiar estado y timers previos si cambia el ID
    setStatus(null);
    setNowPlaying(null);
    setError(null);
    setIsLoading(true);
    errorCountRef.current = 0;

    // Carga inicial
    fetchAll();

    // Timers de polling
    nowPlayingTimerRef.current = setInterval(fetchNowPlaying, NOW_PLAYING_INTERVAL_MS);
    statusTimerRef.current = setInterval(fetchStationStatus, STATION_STATUS_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      if (nowPlayingTimerRef.current) {
        clearInterval(nowPlayingTimerRef.current);
        nowPlayingTimerRef.current = null;
      }
      if (statusTimerRef.current) {
        clearInterval(statusTimerRef.current);
        statusTimerRef.current = null;
      }
    };
  }, [stationId, radioId, activeId, fetchAll, fetchNowPlaying, fetchStationStatus]);

  // ── Page Visibility ──

  useEffect(() => {
    function handleVisibilityChange() {
      isTabVisibleRef.current = document.visibilityState === 'visible';

      if (document.visibilityState === 'visible' && activeId) {
        errorCountRef.current = 0;
        fetchAll();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [activeId, fetchAll]);

  // ── Derivados ──

  const isStale = nowPlaying?.stale === true || status?.stale === true;

  const onlineLabel: string = (() => {
    const state = status?.provider_status;
    if (!state) return nowPlaying?.online ? 'En vivo' : 'Fuera del aire';
    if (status?.stale) return 'Desactualizado';
    switch (state) {
      case 'ONLINE': return 'En vivo';
      case 'OFFLINE': return 'Fuera del aire';
      case 'PROVIDER_UNAVAILABLE': return 'Proveedor no disponible';
      case 'STALE': return 'Desactualizado';
      default: return String(state);
    }
  })();

  const refresh = useCallback(() => {
    errorCountRef.current = 0;
    setError(null);
    fetchAll();
  }, [fetchAll]);

  return {
    status,
    nowPlaying,
    isLoading,
    error,
    isStale,
    onlineLabel,
    refresh,
  };
}